import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { symptomsFormatter } from '@/entities/study';
import { zeroPad, dateToStringWithDailyTimeZone } from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';

/**
 * Builds the header fields shown on an event card in the study management events tab. Emits the zero-padded event friendly id, the event type, and the trigger time rendered in the study's daily timezone via dateToStringWithDailyTimeZone, substituting the placeholder character when the event has no start.
 *
 * @param data - Study context supplying start, timezone, studyTimeOffset and facility.
 * @param event - Event whose friendly id, type and start time are displayed.
 * @returns Array of title and content pairs for the card header.
 */
const generateTitleBlockArray = (data: any, event: any) => [
  {
    title: t`Event ID`,
    content: zeroPad(event?.friendlyId),
  },
  {
    title: t`Event type`,
    content: event?.type,
  },
  {
    title: t`Trigger time`,
    content: event?.start
      ? dateToStringWithDailyTimeZone({
          startStudyDate: data.start,
          isoDate: event?.start,
          timezone: data.timezone,
          studyTimeOffset: data.studyTimeOffset,
          facility: data.facility,
        })
      : DOUBLE_HYPHEN_BULLET,
  },
];

/**
 * Assembles the full view model of one event card in the study management events tab. Combines the header title blocks with a single 'View ECG' button bound to the event id, and builds the info section from the formatted symptom reasons, the comma-joined comments, the strip images with the combined 'all.svg' entry filtered out, a no-ECG flag taken from the absence of realtime data, and the event's study.
 *
 * @param data - Study context passed through to the header title blocks.
 * @param event - Event supplying reasons, comments, strip images, realtime data and study.
 * @param handleClickViewECG - Callback invoked with the event id when the View ECG button is clicked.
 * @returns View model holding the card's header data and info data.
 */
const createEventListItemData = ({ data, event, handleClickViewECG }: any) => ({
  headerData: {
    titleBlockArray: generateTitleBlockArray(data, event),
    buttonList: [
      {
        name: t`View ECG`,
        canBeDisabled: false,
        handleClick: () => {
          handleClickViewECG(event.id);
        },
      },
    ],
  },
  infoData: {
    symptoms: symptomsFormatter(event.reason),
    comments: _.join(event?.comments || [], ','),
    stripImgs: _.filter(event.stripImgs, (x) => !_.includes(x, 'all.svg')), // *: Remove all strip from images
    isNoEcgData: !event?.realtimeData?.start,
    study: event?.study,
  },
});

export { createEventListItemData };
